import DocumentViewerModal from "components/DocumentViewer";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import AddCouncilOfficeVerdictInitialDay from "pages/appealRequests/fragments/AddCouncilOfficeVerdictInitialDay";
import InitialDecisionCard from "pages/appealRequests/fragments/InitialDecisionCard";
import { useState } from "react";
import {
  addCouncilLitigation,
  editCouncilLitigation2,
} from "services/prosecutorDecipilineService";
import { AccusedSummon, Complaints, CouncilLitigation } from "types";
import { COMPLIANT_STATUS } from "utils/constantValues";
import { ROLES } from "utils/roles";
import AddCouncilMeetingAttendance from "./AddCouncilMeetingAttendance";
import RejectLitigationModal from "./RejectLitigationModal";
import UploadCouncilClaimFile from "./UploadCouncilClaimFile";

interface CouncilLitigationProps {
  complaintData: Complaints;
  accusedSummonData?: AccusedSummon;
  councilLitigationData: CouncilLitigation[];
  onCouncilLitigationRefresh: any;
  onLitigationRefresh: any;
}

const calculate_remaining_days = ({ date }: { date: string }) => {
  if (!date) return null;

  const appeal = new Date(date);
  const today = new Date();

  // Zero out time to compare dates only
  appeal.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const diffInMs = appeal.getTime() - today.getTime();

  const remainingDays = Math.floor(diffInMs / msPerDay);
  return remainingDays;
};

const getHearingStatus = (obj: any) => {
  const hasNextAppointment = obj?.next_appointment_date;
  const hasAppointmentReason = obj?.appointment_reason;
  const hasDailyReport = obj?.daily_report_summary;

  if (hasNextAppointment && hasAppointmentReason) {
    return {
      type: "upcoming",
      color: "text-red-700",
      bg: "bg-red-50",
      border: "border-red-200",
      icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
    };
  } else if (!hasNextAppointment && hasDailyReport) {
    return {
      type: "completed",
      color: "text-green-600",
      bg: "bg-green-50",
      border: "border-green-200",
      icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
    };
  } else {
    return {
      type: "ongoing",
      color: "text-yellow-600",
      bg: "bg-yellow-50",
      border: "border-yellow-200",
      icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
    };
  }
};

export default function CouncilLitigationCard({
  complaintData,
  councilLitigationData,
  accusedSummonData,
  onCouncilLitigationRefresh,
  onLitigationRefresh,
}: CouncilLitigationProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const canViewDailyHearing = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canAddMeetingAttendance = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canCreateAppealSummon = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
  ]);

  const [isOpen, setIsOpen] = useState(false);
  const [isUploadFileOpen, setIsUploadFileOpen] = useState(false);
  const [selectedLitigation, setSelectedLitigation] = useState<any>(null);
  const [isInitialHearingDecisionOpen, setIsInitialHearingDecisionOpen] =
    useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [expandedReports, setExpandedReports] = useState<Set<string>>(
    new Set(),
  );
  const [expandedAttendances, setExpandedAttendances] = useState<Set<string>>(
    new Set(),
  );
  const [editingDateId, setEditingDateId] = useState<string | null>(null);
  const [dateDraft, setDateDraft] = useState({
    hearing_date: "",
    next_appointment_date: "",
  });
  const [isDateUpdating, setIsDateUpdating] = useState(false);

  const toDateInputValue = (value?: string) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const toggleReport = (id: string) => {
    const newExpanded = new Set(expandedReports);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedReports(newExpanded);
  };

  const toggleAttendance = (id: string) => {
    const newExpanded = new Set(expandedAttendances);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedAttendances(newExpanded);
  };

  const startDateEdit = (item: CouncilLitigation) => {
    setEditingDateId(item.id ?? null);
    setDateDraft({
      hearing_date: toDateInputValue(item.hearing_date as string),
      next_appointment_date: toDateInputValue(
        item.next_appointment_date as string,
      ),
    });
  };

  const cancelDateEdit = () => {
    setEditingDateId(null);
    setDateDraft({ hearing_date: "", next_appointment_date: "" });
  };

  const saveDateEdit = async (item: CouncilLitigation) => {
    if (!item?.id) {
      showToast("Litigation id not found", "error");
      return;
    }

    const currentHearingDate = toDateInputValue(item.hearing_date as string);
    const currentNextDate = toDateInputValue(
      item.next_appointment_date as string,
    );

    const payload: Record<string, any> = { id: item.id };

    if (
      dateDraft.hearing_date &&
      dateDraft.hearing_date !== currentHearingDate
    ) {
      payload.hearing_date = dateDraft.hearing_date;
    }

    if (
      dateDraft.next_appointment_date &&
      dateDraft.next_appointment_date !== currentNextDate
    ) {
      payload.next_appointment_date = dateDraft.next_appointment_date;
    }

    if (!payload.hearing_date && !payload.next_appointment_date) {
      showToast("No date change detected", "error");
      return;
    }

    try {
      setIsDateUpdating(true);
      await editCouncilLitigation2(payload as any, payload.id);
      await Promise.resolve(onCouncilLitigationRefresh?.());
      cancelDateEdit();
      showToast("Date updated", "success");
    } catch (err) {
      showToast("Failed to update date", "error");
    } finally {
      setIsDateUpdating(false);
    }
  };

  const councilDecision =
    councilLitigationData &&
    councilLitigationData?.length > 0 &&
    (councilLitigationData[0]?.council_decision_documents?.filter(
      (a: any) => a?.document_type === "council_decision",
    ) as any);

  const claimSummary =
    councilLitigationData &&
    councilLitigationData?.length > 0 &&
    (councilLitigationData[0]?.council_decision_documents?.filter(
      (a: any) => a?.document_type === "claim_summary_file",
    ) as any);

  // Filter and sort daily reports
  const dailyReports = [...(councilLitigationData || [])]
    ?.filter(
      (obj) =>
        !obj.council_decision_day &&
        (canViewDailyHearing || obj?.council_decision_summary),
    )
    ?.sort((a, b) => {
      const dateA = new Date(
        a?.next_appointment_date || a?.hearing_date || "",
      ).getTime();
      const dateB = new Date(
        b?.next_appointment_date || b?.hearing_date || "",
      ).getTime();
      return dateB - dateA; // Most recent first
    });

  return (
    <div className="mb-8">
      {/* Header Section */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-800 border-l-4 border-red-600 pl-3 py-1">
          Council Office Proceedings
        </h2>
      </div>

      {/* Final Council Decision */}
      {councilLitigationData[0]?.council_decision_day && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-red-50 to-red-100 border-b border-red-100 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 border border-red-200 rounded-lg">
                <svg
                  className="w-5 h-5 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-800">
                  Final Council Decision
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Official council determination and verdict
                </p>
              </div>
            </div>
          </div>

          <div className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {councilLitigationData[0]?.council_decision_day && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Decision Date
                  </p>
                  <p className="text-sm font-medium text-gray-800">
                    {councilLitigationData[0]?.council_decision_day}
                  </p>
                </div>
              )}

              {councilLitigationData[0]?.council_decision_type && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Decision Type
                  </p>
                  <p className="text-sm font-medium text-gray-800">
                    {councilLitigationData[0]?.council_decision_type}
                  </p>
                </div>
              )}

              {councilLitigationData[0]?.discipline_measure_type && (
                <div className="space-y-1 md:col-span-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Discipline Measure
                  </p>
                  <p className="text-sm font-medium text-gray-800">
                    {councilLitigationData[0]?.discipline_measure_type}
                  </p>
                </div>
              )}
            </div>

            {/* Decision Summary */}
            {councilLitigationData[0]?.council_decision_summary && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Decision Summary
                </h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {councilLitigationData[0]?.council_decision_summary}
                  </p>
                </div>
              </div>
            )}

            {/* Accused Prosecutor */}
            {councilLitigationData[0]?.accused_prosecutor_name && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Accused Prosecutor
                </h4>
                <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-full">
                    <span className="text-sm font-bold text-white">
                      {councilLitigationData[0]?.accused_prosecutor_name
                        .split(" ")
                        .map((n: string) => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-800">
                    {councilLitigationData[0]?.accused_prosecutor_name}
                  </p>
                </div>
              </div>
            )}

            {/* Claim Summary */}
            {councilLitigationData[0]?.claim_summary && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Claim Summary
                </h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {councilLitigationData[0]?.claim_summary}
                  </p>
                </div>
              </div>
            )}

            {/* Response from General Council */}
            {councilLitigationData[0]?.response_from_general_council && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Response from General Council
                </h4>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {councilLitigationData[0]?.response_from_general_council}
                  </p>
                </div>
              </div>
            )}

            {/* Documents */}
            <div className="space-y-4">
              {councilDecision.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Council Decision Documents
                  </h4>
                  <DocumentViewerModal
                    documents={councilDecision}
                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/litigation-counciloffice-documents/document`}
                    layout="list"
                    label="Decision Documents"
                    hideEmptyFile={true}
                  />
                </div>
              )}

              {claimSummary.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    Claim Summary Documents
                  </h4>
                  <DocumentViewerModal
                    documents={claimSummary}
                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/litigation-counciloffice-documents/document`}
                    layout="list"
                    label="Claim Documents"
                    hideEmptyFile={true}
                  />
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col md:flex-row gap-3 mt-6 pt-6 border-t border-gray-200">
              {canCreateAppealSummon &&
                complaintData &&
                !Array.isArray(complaintData) &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData.status !== COMPLIANT_STATUS.TERMINATED &&
                !accusedSummonData &&
                councilLitigationData?.some(
                  (obj) => obj?.is_final_verdict_given,
                ) &&
                claimSummary.length > 0 && (
                  <>
                    <button
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm"
                      onClick={() => setIsInitialHearingDecisionOpen(true)}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Add Initial Hearing Decision
                    </button>
                    <button
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors shadow-sm"
                      onClick={() => setIsRejectOpen(true)}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      Reject Case
                    </button>
                  </>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Daily Hearing Reports */}
      {dailyReports.length > 0 && (
        <div className="mb-6">
          <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            Daily Hearing Reports ({dailyReports.length})
          </h3>

          <div className="space-y-4">
            {dailyReports.map((obj: any) => {
              const status = getHearingStatus(obj);
              const isExpanded = expandedReports.has(obj.id);
              const isAttendanceExpanded = expandedAttendances.has(obj.id);
              const remainingDays = obj?.council_decision_day
                ? calculate_remaining_days({ date: obj.council_decision_day })
                : null;
              const presentCount = obj.attendances
                ? obj.attendances.filter((a: any) => a.was_present).length
                : 0;
              const absentCount = obj.attendances
                ? obj.attendances.length - presentCount
                : 0;

              return (
                <div
                  key={obj.id}
                  className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
                >
                  {/* Report Header */}
                  <button
                    onClick={() => toggleReport(obj.id)}
                    className="w-full text-left p-4 focus:outline-none"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 flex items-center justify-center ${status.bg} ${status.border} rounded-lg`}
                        >
                          <svg
                            className={`w-5 h-5 ${status.color}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d={status.icon}
                            />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-800">
                            {obj?.hearing_date || "Hearing Report"}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className={`text-xs px-2 py-1 rounded ${status.bg} ${status.color} ${status.border}`}
                            >
                              {status.type === "upcoming"
                                ? "Upcoming Hearing"
                                : status.type === "completed"
                                  ? "Completed Hearing"
                                  : "Ongoing Hearing"}
                            </span>
                            {obj?.next_appointment_date && (
                              <span className="text-xs text-gray-500">
                                Next: {obj.next_appointment_date}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <svg
                        className={`w-5 h-5 text-gray-500 transform transition-transform ${isExpanded ? "rotate-180" : "rotate-0"}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </button>

                  {/* Report Content */}
                  {isExpanded && (
                    <div className="p-4 border-t border-gray-100 space-y-4">
                      {/* Key Dates */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {obj?.hearing_date && (
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Hearing Date
                            </p>
                            <p className="text-sm font-medium text-gray-800">
                              {obj.hearing_date}
                            </p>
                          </div>
                        )}

                        {obj?.next_appointment_date && (
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Next Appointment
                            </p>
                            <p className="text-sm font-medium text-gray-800">
                              {obj.next_appointment_date}
                            </p>
                          </div>
                        )}

                        <div className="md:col-span-2 pt-1">
                          {editingDateId === obj.id ? (
                            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    {t("hearingDate")}
                                  </label>
                                  <input
                                    type="date"
                                    value={dateDraft.hearing_date}
                                    onChange={(e) =>
                                      setDateDraft((prev) => ({
                                        ...prev,
                                        hearing_date: e.target.value,
                                      }))
                                    }
                                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    {t("nextAppointmentDate")}
                                  </label>
                                  <input
                                    type="date"
                                    value={dateDraft.next_appointment_date}
                                    onChange={(e) =>
                                      setDateDraft((prev) => ({
                                        ...prev,
                                        next_appointment_date: e.target.value,
                                      }))
                                    }
                                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  disabled={isDateUpdating}
                                  onClick={() => saveDateEdit(obj)}
                                  className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-60"
                                >
                                  {isDateUpdating ? "Updating..." : "Save Date"}
                                </button>
                                <button
                                  type="button"
                                  disabled={isDateUpdating}
                                  onClick={cancelDateEdit}
                                  className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => startDateEdit(obj)}
                              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                            >
                              {t("updateDate")}
                            </button>
                          )}
                        </div>

                        {obj?.given_order && (
                          <div className="space-y-1 md:col-span-2">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t("councilOrder")}
                            </p>
                            <p className="text-sm font-medium text-gray-800">
                              {obj.given_order}
                            </p>
                          </div>
                        )}

                        {remainingDays !== null && (
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t("appealDeadlineStatus")}
                            </p>
                            <div
                              className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium ${
                                remainingDays < 0
                                  ? "bg-red-100 text-red-800 border border-red-200"
                                  : remainingDays <= 7
                                    ? "bg-yellow-100 text-yellow-800 border border-yellow-200"
                                    : "bg-green-100 text-green-800 border border-green-200"
                              }`}
                            >
                              {remainingDays < 0
                                ? `Expired ${Math.abs(remainingDays)} days ago`
                                : `${remainingDays} days remaining`}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Report Content */}
                      {obj?.daily_report_summary && (
                        <div>
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">
                            Daily Report Summary
                          </h5>
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {obj.daily_report_summary}
                            </p>
                          </div>
                        </div>
                      )}

                      {obj?.appointment_reason && (
                        <div>
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">
                            Appointment Reason
                          </h5>
                          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {obj.appointment_reason}
                            </p>
                          </div>
                        </div>
                      )}

                      {obj?.council_decision_summary && (
                        <div>
                          <h5 className="text-sm font-semibold text-gray-700 mb-2">
                            Council Decision Summary
                          </h5>
                          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {obj.council_decision_summary}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Attendance Records */}
                      {obj?.attendances && obj.attendances.length > 0 && (
                        <div>
                          <button
                            onClick={() => toggleAttendance(obj.id)}
                            className="w-full flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none"
                          >
                            <div className="flex items-center gap-3">
                              <svg
                                className="w-5 h-5 text-gray-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                />
                              </svg>
                              <div>
                                <h5 className="text-sm font-semibold text-gray-700">
                                  Attendance Records
                                </h5>
                                <p className="text-xs text-gray-500">
                                  {presentCount} present, {absentCount} absent
                                </p>
                              </div>
                            </div>
                            <svg
                              className={`w-5 h-5 text-gray-500 transform transition-transform ${isAttendanceExpanded ? "rotate-180" : "rotate-0"}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>
                          {isAttendanceExpanded && (
                            <div className="mt-3 space-y-2">
                              {obj.attendances.map((attendance: any) => (
                                <div
                                  key={attendance.id}
                                  className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
                                >
                                  <div className="flex items-center gap-3">
                                    <div
                                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                        attendance.was_present
                                          ? "bg-green-100 text-green-800"
                                          : "bg-red-100 text-red-800"
                                      }`}
                                    >
                                      {attendance.was_present ? "✓" : "✗"}
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-800">
                                        {attendance.council_member}
                                      </p>
                                      {attendance.remark && (
                                        <p className="text-xs text-gray-500">
                                          {attendance.remark}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    {new Date(
                                      attendance.createdAt,
                                    ).toLocaleDateString()}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Documents */}
                      {obj?.council_decision_documents &&
                        obj.council_decision_documents.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold text-gray-700 mb-2">
                              Hearing Documents
                            </h5>
                            <DocumentViewerModal
                              documents={obj.council_decision_documents}
                              baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/litigation-documents/document`}
                              layout="list"
                              label="Hearing Documents"
                              hideEmptyFile={true}
                            />
                          </div>
                        )}

                      {/* Add Attendance Button */}
                      {canAddMeetingAttendance &&
                        !councilLitigationData[0]?.council_decision_day && (
                          <div className="pt-4 border-t border-gray-200">
                            <button
                              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm"
                              onClick={() => {
                                setSelectedLitigation(obj);
                                setIsOpen(true);
                              }}
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M12 4v16m8-8H4"
                                />
                              </svg>
                              Add Attendance Record
                            </button>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Initial Decision Card */}
      {complaintData &&
        !Array.isArray(complaintData) &&
        councilLitigationData &&
        councilLitigationData.length > 0 && (
          <InitialDecisionCard
            complaintData={complaintData}
            councilLitigationData={councilLitigationData[0]}
            onAppealRefresh={onLitigationRefresh}
          />
        )}

      {/* Modals */}
      <AddCouncilMeetingAttendance
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        litigationData={selectedLitigation}
        onRefresh={onCouncilLitigationRefresh}
      />

      <UploadCouncilClaimFile
        isOpen={isUploadFileOpen}
        setIsOpen={setIsUploadFileOpen}
        complaintData={complaintData}
        litigationData={selectedLitigation}
        onRefresh={onCouncilLitigationRefresh}
      />

      {councilLitigationData && Array.isArray(councilLitigationData) && (
        <RejectLitigationModal
          isOpen={isRejectOpen}
          setIsOpen={setIsRejectOpen}
          councilLitigationData={councilLitigationData[0]}
          onComplaintRefresh={onLitigationRefresh}
        />
      )}

      {complaintData &&
        !Array.isArray(complaintData) &&
        councilLitigationData &&
        Array.isArray(councilLitigationData) && (
          <AddCouncilOfficeVerdictInitialDay
            isOpen={isInitialHearingDecisionOpen}
            setIsOpen={setIsInitialHearingDecisionOpen}
            complaintData={complaintData}
            councilLitigationData={councilLitigationData[0]}
            onRefresh={onLitigationRefresh}
          />
        )}
    </div>
  );
}
